/* eslint-disable @typescript-eslint/naming-convention */
import { useCallback, useEffect, useState } from "react"

import AthenaAPI from "services/athena"
import {
  WidgetNamespaceDomain,
  WidgetNamespaceDomains,
} from "types/widget_namespace_domain"
import { cErr } from "utils/debug"

const composeDomainEntity = (domain: string): WidgetNamespaceDomain => ({
  url: domain.trim(),
  created_at: new Date().toISOString(),
})

export const useWidgetDomains = (slug: string) => {
  const [domains, setDomains] = useState<WidgetNamespaceDomains>({
    domains: [],
    version: 0,
  })
  const [isLoading, setLoading] = useState<boolean>(false)

  useEffect(() => {
    const controller = new AbortController()

    const fetchDomains = async () => {
      try {
        const response = await AthenaAPI.get<WidgetNamespaceDomains>(
          `v1/domains/${slug}`,
          {
            signal: controller.signal,
          },
        )
        setDomains(response.data)
      } catch (err) {
        cErr(err)
      }
    }

    fetchDomains()

    return () => {
      controller.abort()
    }
  }, [slug])

  const actions = {
    onCreateDomain: useCallback(
      (domain: string) => {
        const foundIndex = domains?.domains.findIndex(
          (d) => d.url.toLowerCase() === domain.toLowerCase().trim(),
        )
        if (foundIndex !== -1)
          throw new Error("creating already existing domain")

        setDomains((previous) => ({
          ...previous,
          domains: [...previous.domains, composeDomainEntity(domain)],
        }))
      },
      [domains],
    ),

    onRemoveDomain: useCallback((domain: string) => {
      setDomains((previous) => ({
        ...previous,
        domains: previous.domains.filter((d) => d.url !== domain),
      }))
    }, []),

    onUpdateDomains: useCallback(async () => {
      try {
        setLoading(true)
        const { data } = await AthenaAPI.post<WidgetNamespaceDomains>(
          `v1/domains`,
          {
            brand: slug,
            ...domains,
          },
        )

        setDomains(data)
      } catch (e) {
        cErr(e)
        throw e
      } finally {
        setLoading(false)
      }
    }, [domains, slug]),
  }

  return {
    domains,
    actions,
    isLoading,
  }
}
